import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
    if (existing) {
      if (existing.dataset.loaded === 'true' || existing.getAttribute('data-loaded') === 'true') {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => {
      script.dataset.loaded = 'true'
      resolve()
    }
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.body.appendChild(script)
  })
}

function stubCsGlobal() {
  type CsWindow = Window & {
    csGlobal?: {
      rivet: {
        attach: (...args: unknown[]) => void
        util: { getEasing: (name: string) => (t: number) => number }
      }
    }
  }
  const w = window as CsWindow
  if (!w.csGlobal?.rivet) {
    w.csGlobal = {
      rivet: {
        attach() {},
        util: { getEasing: () => (t: number) => t },
      },
    }
  }
}

function toggleCollapse(id: string) {
  const targets = document.querySelectorAll(`[data-x-toggleable="${id}"][data-x-toggle-collapse]`)
  targets.forEach((node) => {
    const collapsed = node.classList.contains('x-collapsed')
    node.classList.toggle('x-collapsed', !collapsed)
    if (node instanceof HTMLElement) {
      node.setAttribute('aria-hidden', collapsed ? 'false' : 'true')
    }
  })
  document.querySelectorAll(`[aria-controls="${id}"], [data-x-toggle="collapse-b"][data-x-toggleable="${id}"]`).forEach((btn) => {
    const expanded = btn.getAttribute('aria-expanded') === 'true'
    btn.setAttribute('aria-expanded', expanded ? 'false' : 'true')
    btn.classList.toggle('collapsed', expanded)
  })
}

function isInternalPath(pathname: string) {
  if (!pathname.startsWith('/')) return false
  if (pathname.startsWith('/assets/')) return false
  if (pathname.startsWith('/wp-')) return false
  return true
}

function isPrerender() {
  return typeof window !== 'undefined' && Boolean(window.__PRERENDER__)
}

export default function ThemeBehaviors() {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (isPrerender()) return
    window.scrollTo(0, 0)
    const mobile = document.getElementById('x-nav-wrap-mobile')
    if (mobile && !mobile.classList.contains('x-collapsed')) {
      toggleCollapse('x-nav-wrap-mobile')
    }
  }, [pathname])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const scrollTop = target.closest<HTMLElement>('.x-scroll-top')
      if (scrollTop) {
        event.preventDefault()
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      const tab = target.closest<HTMLElement>('[data-x-toggle="tab"]')
      if (tab) {
        event.preventDefault()
        const group = tab.getAttribute('data-x-toggle-group')
        const id = tab.getAttribute('data-x-toggleable')
        if (group && id) {
          document.querySelectorAll(`[data-x-toggle-group="${group}"]`).forEach((el) => {
            el.setAttribute('aria-selected', 'false')
            el.parentElement?.classList.remove('active')
          })
          tab.setAttribute('aria-selected', 'true')
          tab.parentElement?.classList.add('active')
          const paneId = tab.getAttribute('aria-controls')
          if (paneId) {
            const pane = document.getElementById(paneId)
            pane?.parentElement?.querySelectorAll('.x-tab-pane, .x-tab-content > *').forEach((p) => {
              p.classList.remove('active', 'in')
            })
            pane?.classList.add('active', 'in')
          }
        }
        return
      }

      const toggle = target.closest<HTMLElement>('[data-x-toggle="collapse-b"], .x-accordion-toggle')
      if (toggle) {
        event.preventDefault()
        const id = toggle.getAttribute('data-x-toggleable') || toggle.getAttribute('aria-controls')
        if (id) toggleCollapse(id)
        return
      }

      const link = target.closest('a')
      if (!link || link.target === '_blank' || link.hasAttribute('download')) return
      const href = link.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return
      let url: URL
      try {
        url = new URL(href, window.location.origin)
      } catch {
        return
      }
      if (url.origin !== window.location.origin) return
      if (!isInternalPath(url.pathname)) return
      event.preventDefault()
      navigate(`${url.pathname}${url.search}${url.hash}`)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [navigate])

  useEffect(() => {
    if (isPrerender()) return
    const counters = [...document.querySelectorAll<HTMLElement>('.x-counter')]
    if (!counters.length) return undefined
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const el = entry.target as HTMLElement
          const raw = el.getAttribute('data-x-element-counter')
          if (!raw) return
          try {
            const cfg = JSON.parse(raw) as { to: string; selector: string }
            const num = el.querySelector(cfg.selector || '.number')
            if (num) num.textContent = cfg.to
          } catch {
            /* keep original markup */
          }
          io.unobserve(el)
        })
      },
      { threshold: 0.3 },
    )
    counters.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [pathname])

  useEffect(() => {
    if (isPrerender()) return
    document.querySelectorAll<HTMLElement>('[data-fade="true"]').forEach((el) => {
      el.style.opacity = '1'
      el.style.transform = 'none'
    })
  }, [pathname])

  useEffect(() => {
    type FlexNode = {
      data: (key: string) => unknown
      flexslider: (opts: Record<string, unknown>) => void
      each: (cb: (i: number, el: Element) => void) => FlexNode
    }
    type JQueryLike = ((sel: string | Element) => FlexNode) & {
      fn: { flexslider?: (opts?: Record<string, unknown>) => void }
    }
    if (isPrerender()) return undefined
    let cancelled = false
    const init = async () => {
      let sliderRoot: Element | null = null
      for (let i = 0; i < 25 && !cancelled; i += 1) {
        sliderRoot = document.querySelector('[data-x-element-slider], .x-flexslider')
        if (sliderRoot) break
        await new Promise((resolve) => setTimeout(resolve, 50))
      }
      if (cancelled || !sliderRoot) return
      stubCsGlobal()
      const w = window as Window & { jQuery?: JQueryLike }
      if (!w.jQuery) await loadScript('/assets/js/jquery.min.js')
      if (cancelled) return
      if (typeof w.jQuery?.fn?.flexslider !== 'function') await loadScript('/assets/js/flexslider.js')
      if (cancelled) return
      const jq = w.jQuery
      if (!jq || typeof jq.fn?.flexslider !== 'function') return
      jq('[data-x-element-slider], .x-flexslider').each((_, el) => {
        const node = jq(el)
        if (node.data('flexslider')) return
        const raw = el.getAttribute('data-x-element-slider')
        let opts: Record<string, unknown> = {}
        if (raw) {
          try {
            opts = JSON.parse(raw) as Record<string, unknown>
          } catch {
            opts = {}
          }
        }
        node.flexslider({
          selector: '.x-slides > li',
          animation: opts.animation ?? 'slide',
          reverse: Boolean(opts.reverse),
          controlNav: opts.controlNav !== false,
          directionNav: opts.prevNextNav !== false,
          slideshowSpeed: Number(opts.slideTime) || 5000,
          animationSpeed: Number(opts.slideSpeed) || 650,
          slideshow: Boolean(opts.slideshow),
          randomize: Boolean(opts.random),
          touch: opts.touch !== false,
          pauseOnHover: Boolean(opts.pauseOnHover),
        })
      })
    }
    void init()
    return () => {
      cancelled = true
    }
  }, [pathname])

  useEffect(() => {
    if (isPrerender()) return undefined
    const btn = document.querySelector('.x-scroll-top')
    if (!btn) return undefined
    const onScroll = () => {
      const y = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight || 1)
      btn.classList.toggle('is-visible', y >= 0.75)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      if (!(event.target instanceof Element) || !event.target.closest('.x-scroll-top')) return
      event.preventDefault()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('keydown', onKey)
    onScroll()
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  useEffect(() => {
    const form = document.querySelector<HTMLFormElement>('#x-subscribe-form-272')
    if (!form) return undefined
    const onSubmit = (event: Event) => {
      event.preventDefault()
      const data = new FormData(form)
      void fetch('https://conmantheseries.com/wp-admin/admin-ajax.php', {
        method: 'POST',
        body: data,
        credentials: 'omit',
      })
    }
    form.addEventListener('submit', onSubmit)
    return () => form.removeEventListener('submit', onSubmit)
  }, [pathname])

  return null
}
